import { useState, type ReactNode } from "react";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { translate } from "src/utils/string-table";
import {
  Alert,
  AlertColor,
  Breakpoints,
  Button,
  Card,
  Colors,
  DatePickerModal,
  fontMedium,
  fontSmall,
  formatDate,
} from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { ContentSingleColumn } from "~components";
import { createRequestsUrl, createTimesheetEntryEditUrl } from "~pages";

import {
  calculateTotalHours,
  extractBreakPeriodsFromPunches,
  generateWeekId,
  isDateInCurrentWeek,
  type PayCode,
  type TimeCard,
  type TimesheetEntry,
} from "../utils";
import { InteractiveChip } from "./InteractiveChip";
import { TimesheetCard, TimesheetCardProps, TimesheetCardStatus } from "./TimesheetCard";

export const ALERT_CONFIGURATIONS = {
  TIMESHEET_COMPLETE: {
    title: translate("TIMESHEET_SUBMITTED_ALERT_TITLE"),
    status: "success" as AlertColor,
  },
  TIMESHEET_REMINDER: {
    title: translate("TIMESHEET_REMINDER_ALERT_TITLE"),
    description: translate("TIMESHEET_REMINDER_ALERT_DESCRIPTION"),
    status: "info" as AlertColor,
  },
  TIMESHEET_PAST_DUE: {
    title: translate("TIMESHEET_PAST_DUE_ALERT_TITLE"),
    description: (
      <>
        {translate("TIMESHEET_PAST_DUE_ALERT_DESCRIPTION_PREFIX")}{" "}
        <a
          href={generateSwingSupportURL(translate("TIMESHEET_MANUAL_TIMESHEET_ARTICLE"))}
          target="_blank"
          rel="noreferrer"
          css={{ color: Colors.blue500, textDecoration: "underline" }}
        >
          {translate("TIMESHEET_PAST_DUE_ALERT_DESCRIPTION_LINK_TEXT")}
        </a>{" "}
        {translate("TIMESHEET_PAST_DUE_ALERT_DESCRIPTION_SUFFIX")}
      </>
    ),
    status: "warning" as AlertColor,
  },
  TIMESHEET_ERROR: {
    title: translate("TIMESHEET_ERROR_ALERT_TITLE"),
    description: (
      <>
        {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
        <a
          href={generateSwingSupportURL(translate("TIMESHEET_CONTACT_SUPPORT_ARTICLE"))}
          target="_blank"
          rel="noreferrer"
          css={{ color: Colors.blue500, textDecoration: "underline" }}
        >
          {translate("LABEL_CONTACT_SUPPORT")}
        </a>{" "}
        {translate("LABEL_CONTACT_SUPPORT_2")}
      </>
    ),
    status: "danger" as AlertColor,
  },
};

// TODO: Update API types to share this to make sure we always track new
// types
export const PayCodeLabelMapping: Record<string, string> = {
  Sick: translate("TIMESHEET_PAYCODE_SICK_LEAVE_LABEL"),
  NJSIC: translate("TIMESHEET_PAYCODE_SICK_LEAVE_LABEL"),
  "Mandated Reporter Training": translate("TIMESHEET_PAYCODE_MRT_LABEL"),
  "Admin New Hire Paperwork": translate("TIMESHEET_PAYCODE_COC_LABEL"),
  "Harassment Prevention Training": translate("TIMESHEET_PAYCODE_HPT_LABEL"),
};

/***** Props Types *****/
export type TimesheetViewProps = {
  /** The current date and time */
  now: DateTime;
  /** The selected date to display */
  selectedDate: DateTime;
  /**
   * The timesheet entries to display
   *
   * @default []
   */
  records?: Array<TimesheetEntry>;
  /** Triggered when changing the date */
  onDateChange: (date: string) => void;
  /** Handler when adding a new card */
  onNewCardEntry: () => void;

  /*** Optional ***/
  isError?: boolean;
  isLoading?: boolean;
};

/**
 * Component | TimesheetView
 *
 * View component for the TimeSheet page.
 *
 * TODO: The View component generally should not include the Page template, the
 * parent should include it.
 */
export function TimesheetView(props: TimesheetViewProps) {
  /*** Props ***/
  const {
    now,
    selectedDate,
    records = [],
    isError,
    isLoading,
    onDateChange,
    onNewCardEntry,
  } = props;

  /***** State *****/
  // TODO: This should live in the FilterChip component, or the FilterBar
  // component.
  const [showDateModal, setShowDateModal] = useState(false);
  // TODO: Parent should handle any navigation.
  const history = useHistory();

  // TODO: This should live in the FilterChip component itself.
  function getWeekLabel(params: { now: DateTime; date: DateTime }) {
    const { date, now } = params;

    const { weekStart, weekEnd } = getWeekRange(date);

    // Get current week boundaries with Sunday as start
    const currentWeekStart = now.minus({ days: now.weekday % 7 });
    const currentWeekEnd = currentWeekStart.plus({ days: 6 });

    // Check if the selected week is the current week
    if (weekStart.hasSame(currentWeekStart, "day") && weekEnd.hasSame(currentWeekEnd, "day")) {
      return translate("LABEL_CURRENT_WEEK");
    }

    const startFormatted = formatDate({ date: weekStart, format: "Jan 1, 2025" });
    const endFormatted = formatDate({ date: weekEnd, format: "Jan 1, 2025" });

    return `${startFormatted} - ${endFormatted}`;
  }

  const hasRecords = records ? records.length > 0 : false;
  const isSelectedDateInCurrentWeek = isDateInCurrentWeek({
    now,
    date: selectedDate,
  });

  // Determines if ALL timecards are completed in the UI.
  const isTimesheetComplete = hasRecords && records.every(isTimesheetEntryComplete);

  /** Handler for editing a timesheet entry */
  function handleEditEntry(index: number) {
    const weekId = generateWeekId(selectedDate);
    const indexId = index.toString();
    history.push(createTimesheetEntryEditUrl(weekId, indexId));
  }

  /***** Render *****/
  return (
    <ScorePage title={translate("TIMESHEET_TITLE")} isLoading={isLoading} hasBack>
      <div
        css={{
          [Breakpoints.mediumDeviceAndUp]: {
            "& > div": {
              // Target the ContentSingleColumn's inner div
              paddingTop: "0px !important",
            },
          },
        }}
      >
        <ContentSingleColumn>
          <div css={{ padding: 16, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Filter */}
            <div
              css={{
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
              }}
            >
              <span
                css={{
                  ...fontMedium(),
                  whiteSpace: "nowrap",
                  marginRight: "8px",
                  "@media (max-width: 400px)": {
                    ...fontSmall(),
                  },
                }}
              >
                {translate("TIMESHEET_VIEWING_FOR")}
              </span>

              <InteractiveChip
                color="WHITE200"
                label={getWeekLabel({ now, date: selectedDate })}
                onClickHandler={() => setShowDateModal(true)}
              />
            </div>

            {/* Alerts */}
            <TimesheetAlert
              isTimesheetComplete={isTimesheetComplete}
              isSelectedDateInCurrentWeek={isSelectedDateInCurrentWeek}
              isError={isError}
            />

            {/* Records */}
            {hasRecords && (
              <div css={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {records.map((record, index) => {
                  const cardProps = transformTimesheetEntryToTimesheetCardProps({
                    timesheetEntry: record,
                    now,
                  });

                  // Create a unique key for each record
                  const key =
                    record.type === "TIMECARD"
                      ? // `timecard-swingedu-testschool-1-2025-07-22`
                        `timecard-${record.school.name.toLowerCase().split(" ").join("-")}-${record.date.toISODate()}`
                      : `paycode-${record.id}`;

                  return (
                    <div key={key}>
                      <TimesheetCard {...cardProps} onEdit={() => handleEditEntry(index)} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {!hasRecords && (
              <div
                css={{
                  paddingTop: 32,
                  paddingBottom: 32,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  textAlign: "center",
                }}
              >
                {/* Bolded Text */}
                <p css={{ ...fontMedium({ fontWeight: 600 }), margin: 0 }}>
                  {translate("TIMESHEET_NO_REQUESTS_FOR_SELECTED_WEEK")}
                </p>
                {/* Sub Heading */}
                <p css={{ ...fontMedium(), margin: 0 }}>
                  <a
                    css={{ color: Colors.blue500, textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => history.push(createRequestsUrl())}
                  >
                    {translate("TIMESHEET_EXPLORE_OPENINGS")}
                  </a>{" "}
                  {translate("TIMESHEET_EXPLORE_OPENINGS_RATIONALE")}
                </p>
              </div>
            )}

            {/* "Add Entry" Button */}
            {isSelectedDateInCurrentWeek && (
              <div css={{ display: "flex", justifyContent: "center" }}>
                <Button
                  type="tertiary"
                  onClick={onNewCardEntry}
                  status={!isSelectedDateInCurrentWeek ? "disabled" : "enabled"}
                  icon={{ name: "Add" }}
                >
                  {translate("LABEL_ADD_ENTRY")}
                </Button>
              </div>
            )}

            {/* Total hours */}
            <Card>
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  ...fontMedium({ fontWeight: 600 }),
                }}
              >
                <span>{translate("LABEL_TOTAL_HOURS")}</span>
                <span>{calculateTotalHours(records)}</span>
              </div>
            </Card>
          </div>
        </ContentSingleColumn>

        {/* Date Selection Modal */}
        {/* TODO: Why are we doing Self Invoking Function? */}
        {(() => {
          const { weekEnd } = getWeekRange(now);
          const nextDay = weekEnd.plus({ days: 1 }).toISODate();
          return (
            <DatePickerModal
              value={selectedDate.toISODate()}
              isEnabled={showDateModal}
              disabledDateRanges={[{ start: nextDay }]}
              onChange={(datetime: string) => {
                onDateChange(datetime);
                // Close the modal after the handler is called
                setShowDateModal(false);
              }}
              onClose={() => setShowDateModal(false)}
            />
          );
        })()}
      </div>
    </ScorePage>
  );
}

function TimesheetAlert(props: {
  isTimesheetComplete: boolean;
  isSelectedDateInCurrentWeek: boolean;
  isError?: boolean;
}) {
  const { isTimesheetComplete, isSelectedDateInCurrentWeek, isError } = props;

  const alertConfig = getAlertConfiguration({
    isTimesheetComplete,
    isSelectedDateInCurrentWeek,
    isError,
  });

  if (!alertConfig) {
    return null;
  }

  return (
    <Alert color={alertConfig.status} title={alertConfig.title} showIcon={true}>
      {alertConfig.description && (
        <p css={{ marginTop: 0, marginBottom: 0 }}>{alertConfig.description}</p>
      )}
    </Alert>
  );
}

/***** Helper Functions *****/
// TODO: Test this
export function getAlertConfiguration(params: {
  isTimesheetComplete: boolean;
  isSelectedDateInCurrentWeek: boolean;
  isError?: boolean;
}): { title: string; description?: ReactNode; status: AlertColor } | null {
  const { isTimesheetComplete, isSelectedDateInCurrentWeek, isError } = params;

  let alertConfig;

  if (isError === true) {
    alertConfig = ALERT_CONFIGURATIONS.TIMESHEET_ERROR;
  } else if (isTimesheetComplete && isSelectedDateInCurrentWeek) {
    alertConfig = ALERT_CONFIGURATIONS.TIMESHEET_COMPLETE;
  } else if (isTimesheetComplete && !isSelectedDateInCurrentWeek) {
    alertConfig = { ...ALERT_CONFIGURATIONS.TIMESHEET_COMPLETE, description: undefined };
  } else if (!isTimesheetComplete && isSelectedDateInCurrentWeek) {
    alertConfig = ALERT_CONFIGURATIONS.TIMESHEET_REMINDER;
  } else if (!isTimesheetComplete && !isSelectedDateInCurrentWeek) {
    alertConfig = ALERT_CONFIGURATIONS.TIMESHEET_PAST_DUE;
  } else {
    return null;
  }

  return alertConfig;
}

export function getTimesheetEntryStatus(params: {
  timesheetEntry: TimesheetEntry;
  now: DateTime;
}): TimesheetCardStatus | null {
  const { timesheetEntry, now } = params;

  const isCompleted = isTimesheetEntryComplete(timesheetEntry);

  if (isCompleted) {
    return "SUBMITTED";
  }

  // Determine the date for either entry types
  const endTime =
    timesheetEntry.type === "TIMECARD"
      ? // For TimeCard, use the date field
        timesheetEntry.request.endTime
      : // For PayCode, use date field if available
        timesheetEntry.date;

  // For PayCodes without a date, treat them as being in the current week by default
  const isInCurrentWeek =
    timesheetEntry.type === "PAYCODE" && !endTime
      ? true // PayCodes without dates are considered in current week
      : endTime
        ? isDateInCurrentWeek({ now, date: endTime })
        : false;

  if (!isInCurrentWeek) return "PAST_DUE";

  if (isInCurrentWeek && !endTime) {
    return "ACTION_REQUIRED";
  }

  if (isInCurrentWeek && endTime) {
    if (timesheetEntry.type === "TIMECARD") {
      // For TimeCards, action is required only after the request end time has passed
      return now > endTime ? "ACTION_REQUIRED" : null;
    } else {
      // For PayCodes, action is required if incomplete and in current week
      return "ACTION_REQUIRED";
    }
  }

  return null;
}

// TODO: Is this generic enough of a transformation?
// TODO: Test this
function transformTimeCardToTimesheetCardProps(params: {
  timeCard: TimeCard;
  now: DateTime;
}): TimesheetCardProps {
  const { timeCard, now } = params;

  /*** Constants ***/
  // Use the shared utility function to extract work and break periods
  const { workPeriod, breakPeriods } = extractBreakPeriodsFromPunches(timeCard.entries);

  const cardStatus = getTimesheetEntryStatus({ timesheetEntry: timeCard, now });

  // Convert break periods to the format expected by TimesheetCard
  const formattedBreakPeriods = breakPeriods.map((breakPeriod) => ({
    start: breakPeriod.start,
    end: breakPeriod.end,
  }));

  return {
    requestDuration: {
      start: timeCard.request.startTime,
      end: timeCard.request.endTime,
    },
    workDuration: {
      start: workPeriod.start,
      end: workPeriod.end,
    },
    breakPeriods: formattedBreakPeriods,
    date: timeCard.date,
    location: {
      id: timeCard.school.id,
      name: timeCard.school.name,
    },
    status: cardStatus,
  };
}

export function transformPayCodeToTimesheetCardProps(params: {
  payCode: PayCode;
  now: DateTime;
}): TimesheetCardProps {
  const { payCode, now } = params;

  // Convert durationInMinutes to decimal hours for display
  const totalHours = payCode.durationInMinutes
    ? (payCode.durationInMinutes / 60).toFixed(2)
    : undefined;

  // Use date field if available
  const entryDate = payCode.date;

  return {
    date: entryDate,
    payCodeName: PayCodeLabelMapping[payCode.payCode || ""],
    totalHours,
    status: getTimesheetEntryStatus({ timesheetEntry: payCode, now }),
  };
}

function transformTimesheetEntryToTimesheetCardProps(params: {
  timesheetEntry: TimesheetEntry;
  now: DateTime;
}): TimesheetCardProps {
  const { timesheetEntry, now } = params;

  if (timesheetEntry.type === "TIMECARD") {
    return transformTimeCardToTimesheetCardProps({ timeCard: timesheetEntry, now });
  } else {
    return transformPayCodeToTimesheetCardProps({ payCode: timesheetEntry, now });
  }
}

function getWeekRange(date: DateTime): { weekStart: DateTime; weekEnd: DateTime } {
  // Start week on Sunday
  const weekStart = date.minus({ days: date.weekday % 7 });
  const weekEnd = weekStart.plus({ days: 6 });
  return { weekStart, weekEnd };
}

/***** Helper Functions *****/
/**
 * Determines if a timesheet entry is complete.
 *
 * A TimeCard is complete when there is at least one PUNCH_IN entry with valid
 * ts and id, AND at least one PUNCH_OUT entry with valid ts and id.
 *
 * A PayCode is complete when there is a valid `id` and `durationInMinutes` value
 * is present and has a positive value.
 */
export function isTimesheetEntryComplete(record: TimesheetEntry): boolean {
  if (record.type === "TIMECARD") {
    // Find all punch-in entries that have both a timestamp and ID
    // NOTE: The truthy checks (entry.ts && entry.id) filter out null, undefined,
    // and empty strings
    const punchInEntries = record.entries.filter(
      (entry) => entry.type === "PUNCH_IN" && entry.ts && entry.id,
    );

    // Find all punch-out entries that have both a timestamp and ID
    // NOTE: The truthy checks (entry.ts && entry.id) filter out null, undefined,
    // and empty strings
    const punchOutEntries = record.entries.filter(
      (entry) => entry.type === "PUNCH_OUT" && entry.ts && entry.id,
    );

    // TimeCard is complete only if we have at least one valid punch-in AND one
    // valid punch-out
    return punchInEntries.length > 0 && punchOutEntries.length > 0;
  } else if (record.type === "PAYCODE") {
    // PayCode is complete when it has an ID and a positive duration
    return !!(record.id && record.durationInMinutes && record.durationInMinutes > 0);
  }

  // Unknown record types are considered incomplete
  return false;
}
